/**
 * 底部面板。
 *
 * 用 Portal 挂到 body 上，而不是就地渲染：页面切换动画会给内容加 transform，
 * 而带 transform 的祖先会改变 fixed 定位的参照物，面板会跟着页面错位。
 * 挂到 body 就完全不受影响。
 *
 * 组件只负责"什么时候挂载/卸载"，动画全交给 motion 里的 useSheet。
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { useSheet } from '../motion';
import { IconGlyph, GLYPHS } from './IconGlyph';
import { Pressable } from './Pressable';

export interface SheetProps {
	open: boolean;
	onClose: () => void;
	title: string;
	/** 面板主体，可滚动 */
	children: ReactNode;
	/** 固定在底部的操作区 */
	footer?: ReactNode;
	/**
	 * 退场动画播完、真正卸载之后触发。
	 * 编辑类面板用它来清空"正在编辑的这条数据"：
	 * 如果一关就把数据清掉，面板在滑下去的过程中会变成空白，很突兀。
	 */
	onExited?: () => void;
}

export function Sheet({
	open,
	onClose,
	title,
	children,
	footer,
	onExited,
}: SheetProps): JSX.Element | null {
	// 先挂载再播入场动画；退场动画播完才真正卸载
	const [mounted, setMounted] = useState(open);
	const exitedRef = useRef(onExited);
	exitedRef.current = onExited;

	useEffect(() => {
		if (open) setMounted(true);
	}, [open]);

	const { sheetRef, backdropRef } = useSheet({
		open,
		mounted,
		onClose,
		onExited: () => {
			setMounted(false);
			exitedRef.current?.();
		},
	});

	if (!mounted) return null;

	return createPortal(
		<div className="fixed inset-0 z-50">
			<div
				ref={backdropRef}
				onClick={onClose}
				aria-hidden
				className="absolute inset-0 bg-black/35"
				style={{ opacity: 0 }}
			/>

			<div
				ref={sheetRef}
				role="dialog"
				aria-modal="true"
				aria-label={title}
				className="absolute inset-x-0 bottom-0 flex max-h-[92dvh] flex-col overflow-hidden rounded-t-sheet bg-surface shadow-e2 will-change-transform"
				style={{ transform: 'translateY(100%)' }}
			>
				{/* 拖拽把手。data-sheet-grab 是 useSheet 的挂载点，不要删。 */}
				<div
					data-sheet-grab
					className="flex shrink-0 cursor-grab touch-none justify-center pb-1 pt-3 active:cursor-grabbing"
				>
					<span className="h-1 w-9 rounded-full bg-label/20" />
				</div>

				<header className="flex shrink-0 items-center justify-between px-5 pb-3 pt-1">
					<h2 className="text-[17px] font-semibold text-label">{title}</h2>
					<Pressable
						aria-label="关闭"
						onClick={onClose}
						className="flex h-8 w-8 items-center justify-center rounded-full bg-surface-2 text-label-2"
					>
						<IconGlyph glyph={GLYPHS.close} size={15} />
					</Pressable>
				</header>

				<div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-4">
					{children}
				</div>

				{footer ? (
					<footer className="shrink-0 border-t border-separator px-5 pb-[max(0.875rem,env(safe-area-inset-bottom))] pt-3">
						{footer}
					</footer>
				) : (
					<div className="h-[env(safe-area-inset-bottom)] shrink-0" />
				)}
			</div>
		</div>,
		document.body,
	);
}
